import { useEffect, useRef, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import {
  useAddresses,
  useAddressPools,
  useAuthContext,
  useDucks,
} from '../../hooks';
import { Address, Duck } from '../../hooks/api/types';
import { LoadError, LoadingSpinner, Tag } from '../shared';
import { getAllAddresses } from './utils';
import { useTitleCase } from 'use-title-case';

export function Pools() {
  const { token, isAuthenticated, logout } = useAuthContext();
  const { colorScheme } = useMantineColorScheme();
  const titleCase = useTitleCase();
  const { pools, loading, error, fetchPools } = useAddressPools(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    {
      autoFetch: false,
      pageSize: 25,
    },
  );

  const hasFetchedRef = useRef(false);
  const [opened, { toggle }] = useDisclosure();
  const [selectedAddress, setSelectedAddress] = useState<{
    ip: string;
    poolId: number;
  } | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  // Address details hooks (conditionally initialize)
  const addressDetails = useAddresses(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    { poolId: selectedAddress?.poolId || 1 }, // Use poolId 1 as default, but we'll only fetch when needed
  );

  const duckDetails = useDucks(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    {
      autoFetch: false, // Don't auto-fetch ducks
    },
  );

  const handleAddressClick = (address: string, poolId: number) => {
    setSelectedAddress({ ip: address, poolId });
    openModal();
  };

  const handleModalClose = () => {
    closeModal();
    setSelectedAddress(null);
  };

  useEffect(() => {
    if (isAuthenticated && token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPools();
    }
  }, [isAuthenticated, token, fetchPools]);

  if (loading) return <LoadingSpinner />;
  if (error) return <LoadError failedItem="pools" error={error} />;

  return (
    <Container>
      <Button size="sm" onClick={toggle} mb="md">
        {opened ? 'Hide' : 'Show'} Addresses
      </Button>
      <SimpleGrid cols={2}>
        {pools.map((pool) => {
          const availableAddresses = getAllAddresses(pool);
          const usedCount = pool.addresses?.length || 0;
          const totalCount = availableAddresses.length;
          const usagePercentage =
            totalCount > 0 ? (usedCount / totalCount) * 100 : 0;

          return (
            <Card
              key={pool.id}
              shadow="xl"
              padding="xl"
              radius="lg"
              withBorder
              style={{
                background:
                  colorScheme === 'dark'
                    ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderColor: colorScheme === 'dark' ? '#64748b' : '#cbd5e1',
              }}
            >
              <Card.Section p="xl" mb="lg">
                <Group justify="space-between" align="center" mb="lg">
                  <Title
                    order={2}
                    mb="0"
                    style={{
                      background:
                        colorScheme === 'dark'
                          ? 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)'
                          : 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {titleCase(pool.name.replace(/_/g, ' '))}
                  </Title>
                  <RingProgress
                    label={
                      <Text size="xs" ta="center" fw={600}>
                        {usedCount}/{totalCount}
                      </Text>
                    }
                    size={60}
                    thickness={4}
                    sections={[
                      {
                        value: usagePercentage,
                        color:
                          usagePercentage > 80
                            ? 'accent.6'
                            : usagePercentage > 50
                              ? 'orange.6'
                              : 'tealish.5',
                      },
                    ]}
                  />
                </Group>
                <Group justify="space-between" mb="md">
                  <Text fw={600} size="sm" c="dimmed">
                    Start IP
                  </Text>
                  <Text ff="monospace" size="sm" fw={500}>
                    {pool.start_ip}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600} size="sm" c="dimmed">
                    End IP
                  </Text>
                  <Text ff="monospace" size="sm" fw={500}>
                    {pool.end_ip}
                  </Text>
                </Group>
              </Card.Section>
              {opened && (
                <Group gap="xs">
                  {availableAddresses.map((address) => {
                    const isUsed = pool.addresses?.includes(address);
                    return (
                      <Badge
                        key={address}
                        variant={isUsed ? 'gradient' : 'light'}
                        gradient={
                          isUsed
                            ? { from: 'tealish.4', to: 'tealish.6', deg: 45 }
                            : undefined
                        }
                        color={isUsed ? undefined : 'slate'}
                        size="sm"
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          fontWeight: isUsed ? 600 : 400,
                          cursor: isUsed ? 'pointer' : 'default',
                        }}
                        onClick={
                          isUsed
                            ? () => handleAddressClick(address, pool.id)
                            : undefined
                        }
                      >
                        {address}
                      </Badge>
                    );
                  })}
                </Group>
              )}
            </Card>
          );
        })}
      </SimpleGrid>

      <Modal
        opened={modalOpened}
        onClose={handleModalClose}
        title="Address Details"
        size="lg"
      >
        <AddressDetailsModal
          selectedAddress={selectedAddress}
          addressDetails={addressDetails}
          duckDetails={duckDetails}
        />
      </Modal>
    </Container>
  );
}

interface AddressDetailsModalProps {
  selectedAddress: { ip: string; poolId: number } | null;
  addressDetails: ReturnType<typeof useAddresses>;
  duckDetails: ReturnType<typeof useDucks>;
}

function AddressDetailsModal({
  selectedAddress,
  addressDetails,
  duckDetails,
}: AddressDetailsModalProps) {
  const [addressData, setAddressData] = useState<Address | null>(null);
  const [duck, setDuck] = useState<Duck | null>(null);

  useEffect(() => {
    if (selectedAddress && addressDetails) {
      // Reset previous data
      setAddressData(null);
      setDuck(null);

      // Fetch all addresses from the pool to find the matching one
      addressDetails
        .fetchAddresses(1, 1000) // Get a large page to find our address
        .then((response) => {
          const foundAddress = response.results.find(
            (addr) => addr.ip === selectedAddress.ip,
          );

          if (foundAddress) {
            // Now fetch the specific address by ID to get complete details
            addressDetails
              .getAddress(foundAddress.id)
              .then((addressData) => {
                setAddressData(addressData);

                if (addressData.duck) {
                  // Fetch the duck details using the duck ID
                  duckDetails
                    .getDuck(addressData.duck)
                    .then((duckData) => {
                      setDuck(duckData);
                    })
                    .catch((error) => {
                      console.error('Failed to fetch duck details:', error);
                      setDuck(null);
                    });
                } else {
                  setDuck(null);
                }
              })
              .catch((error) => {
                console.error(
                  'Failed to fetch specific address details:',
                  error,
                );
                setAddressData(null);
                setDuck(null);
              });
          } else {
            setAddressData(null);
            setDuck(null);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch addresses:', error);
          setAddressData(null);
          setDuck(null);
        });
    } else {
      // Clear data when no address is selected
      setAddressData(null);
      setDuck(null);
    }
  }, [selectedAddress]);

  if (!selectedAddress) {
    return <Text>No address selected</Text>;
  }

  if (addressDetails.loading) {
    return <Text>Loading address details...</Text>;
  }

  if (!addressData) {
    return <Text>Address not found</Text>;
  }

  return (
    <Stack gap="md">
      <Text size="lg" fw={600}>
        IP Address: {addressData.ip}
      </Text>

      {addressData.lease_expires && (
        <Text>
          Lease Expires:{' '}
          {new Date(addressData.lease_expires).toLocaleDateString()}
        </Text>
      )}

      {duck ? (
        <Stack gap="sm">
          <Text size="md" fw={600}>
            Associated Device:
          </Text>
          <Text>Name: {duck.name}</Text>
          {duck.mac_address && <Text>MAC Address: {duck.mac_address}</Text>}
          {duck.tags && duck.tags.length > 0 && (
            <div>
              <Text fw={600} mb="xs">
                Tags:
              </Text>
              <Group gap="xs">
                {duck.tags.map((tag: string) => (
                  <Tag key={tag} tag={tag} />
                ))}
              </Group>
            </div>
          )}
          {duck.additional_info && (
            <div>
              <Text fw={600} mb="xs">
                Additional Info:
              </Text>
              <Text size="sm" c="dimmed">
                {JSON.stringify(duck.additional_info, null, 2)}
              </Text>
            </div>
          )}
        </Stack>
      ) : (
        <Text c="dimmed">No device associated with this address</Text>
      )}
    </Stack>
  );
}

export default Pools;
